type RemoveBrandPrefixParameters = {
  brand: string;
  text: string;
  fitIndicators?: readonly string[] | string[];
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getFitIndicatorSuffix = (
  brand: string,
  fitIndicators: readonly string[],
): string | null => {
  const found = fitIndicators.find((indicator) =>
    new RegExp(`\\b${escapeRegExp(indicator)}$`, "i").test(brand),
  );

  return found ?? null;
};

// Collaboration titles read "Topshop x Cara ...". Stripping the brand there
// leaves a dangling "X Cara ...", so the brand has to stay.
const COLLABORATION_SEPARATORS = ["x", "\u00d7"];

const isCollaboration = (brand: string, text: string): boolean => {
  const separators = COLLABORATION_SEPARATORS.join("");

  return new RegExp(
    `^${escapeRegExp(brand)}\\s+[${separators}](\\s|$)`,
    "i",
  ).test(text);
};

const stripPrefix = (brand: string, text: string): string => {
  if (isCollaboration(brand, text)) {
    return text;
  }

  const regex = new RegExp(`^${escapeRegExp(brand)}\\s*(\\w)`, "i");
  return text.replace(regex, (_, firstLetter) => firstLetter.toUpperCase());
};

export const removeBrandPrefix = ({
  brand,
  text,
  fitIndicators = [],
}: RemoveBrandPrefixParameters): string => {
  if (!brand) {
    return text;
  }

  const fitIndicator = getFitIndicatorSuffix(brand, fitIndicators);

  if (fitIndicator) {
    const coreBrand = brand.slice(0, brand.length - fitIndicator.length).trim();

    // "Topshop Petite x Cara ..." collaborates under the full sub-brand name,
    // which stripPrefix would not see because it only gets the core brand.
    if (!coreBrand || isCollaboration(brand, text)) {
      return text;
    }

    return stripPrefix(coreBrand, text);
  }

  return stripPrefix(brand, text);
};
